import { useContext, useEffect, useState } from 'react';
import useSettings from '../useSettings';
import { getPublicImageUrl } from '../../config';
import ColorModeContext from '../../layout/themeContext';
import api from '../../services/api';
import {
  PWA_BRANDING_KEYS,
  PWA_THEME_COLOR_DEFAULT,
  PWA_BACKGROUND_COLOR_DEFAULT,
  getPwaIconApiPath,
  normalizePwaIconResponse,
} from '../../constants/pwaBranding';

/** Fallback icon when no configured PWA icon (matches index.html apple-touch-icon). */
const DEFAULT_ICON_URL =
  typeof window !== 'undefined' ? `${window.location.origin}/icons/icon-192.png` : '';

/**
 * Returns tenant PWA branding for install prompt, manifest-style UI and <link rel="apple-touch-icon">.
 * - Name/colors: from public settings (fallback ColorMode / defaults for non-install UI).
 * - Icon URLs: from GET /pwa/icon (global) + public settings.
 *
 * Install prompt name/icon (installPromptDisplayName, installPromptIconUrl) use only:
 * 1. Configured PWA branding (public settings pwaName/pwaShortName, company PWA icon)
 * 2. Manifest default (PingoChat, /icons/icon-192.png)
 * 3. Static fallback (App, favicon)
 * Never document.title or colorMode.appName for the install modal.
 */
export function usePWABranding() {
  const { getPublicSetting } = useSettings();
  const colorMode = useContext(ColorModeContext)?.colorMode;
  const [branding, setBranding] = useState({
    pwaName: '',
    pwaShortName: '',
    pwaDescription: '',
    pwaIconUrl: '',
    pwaAppleTouchIconUrl: '',
    pwaIcon192Url: '',
    pwaIcon512Url: '',
    pwaThemeColor: PWA_THEME_COLOR_DEFAULT,
    pwaBackgroundColor: PWA_BACKGROUND_COLOR_DEFAULT,
    loaded: false,
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [name, shortName, description, icon, themeColor, bgColor, appNameFallback] =
          await Promise.all([
            getPublicSetting(PWA_BRANDING_KEYS.APP_NAME),
            getPublicSetting(PWA_BRANDING_KEYS.SHORT_NAME),
            getPublicSetting(PWA_BRANDING_KEYS.DESCRIPTION),
            getPublicSetting(PWA_BRANDING_KEYS.ICON),
            getPublicSetting(PWA_BRANDING_KEYS.THEME_COLOR),
            getPublicSetting(PWA_BRANDING_KEYS.BACKGROUND_COLOR),
            getPublicSetting('appName'), // fallback quando pwaName vazio (ex.: usuário configurou só na aba Personalizar)
          ]);
        if (cancelled) return;
        const rawName = (name?.value ?? name ?? '').trim();
        const rawAppName = (appNameFallback?.value ?? appNameFallback ?? '').trim();
        const pwaName = rawName || rawAppName;
        const pwaShortName = (shortName?.value ?? shortName ?? '').trim();
        const pwaDescription = (description?.value ?? description ?? '').trim();
        const pwaIcon = (icon?.value ?? icon ?? '').trim();
        const pwaThemeColor =
          (themeColor?.value ?? themeColor ?? '').trim() || PWA_THEME_COLOR_DEFAULT;
        const pwaBackgroundColor =
          (bgColor?.value ?? bgColor ?? '').trim() || PWA_BACKGROUND_COLOR_DEFAULT;
        // blob/data URLs são temporárias; não usar como ícone nem enviar ao backend
        const isBlobOrData =
          pwaIcon && (pwaIcon.startsWith('blob:') || pwaIcon.startsWith('data:'));
        setBranding((prev) => ({
          ...prev,
          pwaName,
          pwaShortName,
          pwaDescription,
          pwaIconUrl: pwaIcon && !isBlobOrData ? getPublicImageUrl(pwaIcon) : prev.pwaIconUrl,
          pwaThemeColor: pwaThemeColor.startsWith('#') ? pwaThemeColor : `#${pwaThemeColor}`,
          pwaBackgroundColor: pwaBackgroundColor.startsWith('#')
            ? pwaBackgroundColor
            : `#${pwaBackgroundColor}`,
          loaded: true,
        }));
      } catch (_) {
        if (!cancelled) setBranding((prev) => ({ ...prev, loaded: true }));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function fetchIconUrls() {
      try {
        const res = await api.get(getPwaIconApiPath());
        if (cancelled) return;
        const payload = res.data?.data ?? res.data?.result ?? res.data ?? res;
        const {
          iconUrl: url,
          appleTouchIconUrl: appleUrl,
          icon192Url,
          icon512Url,
        } = normalizePwaIconResponse(payload);
        const toFullUrl = (v) => {
          if (!v || typeof v !== 'string') return '';
          const t = v.trim();
          if (t.startsWith('blob:') || t.startsWith('data:')) return t;
          if (t.startsWith('http://') || t.startsWith('https://')) return t;
          return getPublicImageUrl(t);
        };
        const fullIconUrl = toFullUrl(url) || toFullUrl(appleUrl);
        const fullAppleUrl = toFullUrl(appleUrl) || fullIconUrl;
        const fullIcon192Url = toFullUrl(icon192Url);
        const fullIcon512Url = toFullUrl(icon512Url);
        if (fullIconUrl || fullAppleUrl) {
          setBranding((prev) => ({
            ...prev,
            pwaIconUrl: fullIconUrl || prev.pwaIconUrl,
            pwaAppleTouchIconUrl: fullAppleUrl || fullIconUrl || prev.pwaAppleTouchIconUrl,
            pwaIcon192Url: fullIcon192Url || prev.pwaIcon192Url,
            pwaIcon512Url: fullIcon512Url || prev.pwaIcon512Url,
          }));
        }
      } catch (_) {
        // mantém fallback de public setting ou default
      }
    }
    fetchIconUrls();
    return () => {
      cancelled = true;
    };
  }, []);

  const appName = colorMode?.appName?.trim() || '';
  const displayName = branding.pwaName || appName || 'App';
  const displayShortName = branding.pwaShortName || displayName.slice(0, 20);
  const description = branding.pwaDescription || 'Atendimento e gestão de conversas em um só lugar';
  const iconUrl = branding.pwaIconUrl || branding.pwaAppleTouchIconUrl || DEFAULT_ICON_URL;
  const appleTouchIconUrl = branding.pwaAppleTouchIconUrl || iconUrl;
  const themeColor = branding.pwaThemeColor || PWA_THEME_COLOR_DEFAULT;
  const backgroundColor = branding.pwaBackgroundColor || PWA_BACKGROUND_COLOR_DEFAULT;

  /** For PWA install prompt only: use configured PWA branding, then manifest, never tab/cookie name. */
  const installPromptDisplayName =
    branding.pwaName || branding.pwaShortName || 'PingoChat' || 'App';
  const installPromptIconUrl = iconUrl || DEFAULT_ICON_URL;

  return {
    ...branding,
    displayName,
    displayShortName,
    description,
    iconUrl,
    appleTouchIconUrl,
    icon192Url: branding.pwaIcon192Url || iconUrl,
    icon512Url: branding.pwaIcon512Url || iconUrl,
    themeColor,
    backgroundColor,
    installPromptDisplayName,
    installPromptIconUrl,
  };
}

export default usePWABranding;
